import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import ConfirmModal from '../ui/ConfirmModal';
import { CustomerStatusBadge } from './CustomerStatusBadge';
import { useCustomerPermissions } from '../../hooks/useCustomerPermissions';
import { CUSTOMER_STATUS_LABELS, isCustomerStatus, type CustomerStatus } from '../../lib/customer-status';
import { CRM_ROLES } from '../../lib/roles';

/**
 * Status and owner on the customer page (CRM-FE-1). Both write through
 * PUT /customers/:id with camelCase keys (`status`, `ownerUserId`) — the
 * customers route sanitises against Drizzle property names and answers 400 on
 * a snake_case key.
 *
 * A status change CONFIRMS IN A MODAL and the API's own refusal is shown in
 * that modal verbatim (a sales user's `sales_prospect_only` 403 is a sentence
 * written for the reader, not an error code). The API stamps who changed it
 * and when; this screen does not.
 */
function apiMessage(err: any, fallback: string): string {
  const e = err?.response?.data?.error;
  return typeof e === 'string' && e.trim() ? e : fallback;
}

interface Props {
  customer: { id: string; name?: string; status?: string | null; ownerUserId?: string | null; ownerName?: string | null };
}

export function CustomerAccountStrip({ customer }: Props) {
  const queryClient = useQueryClient();
  const perms = useCustomerPermissions();
  const statusOptions = perms.statusOptions(customer);
  const canPickOwner = perms.canPickOwner(customer);
  const canAssignSelf = perms.canAssignSelf(customer) && customer.ownerUserId !== perms.userId;

  const [statusOpen, setStatusOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState<CustomerStatus | ''>('');
  const [statusError, setStatusError] = useState<string | null>(null);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [nextOwner, setNextOwner] = useState('');
  const [ownerError, setOwnerError] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['customer', customer.id] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const statusMutation = useMutation({
    mutationFn: (status: CustomerStatus) => api.customers.update(customer.id, { status }),
    onSuccess: (_r, status) => {
      invalidate();
      setStatusOpen(false);
      toast.success(`${customer.name || 'Client'} is now ${CUSTOMER_STATUS_LABELS[status]}`);
    },
    onError: (err: any) => setStatusError(apiMessage(err, 'Could not change the status.')),
  });

  const ownerMutation = useMutation({
    mutationFn: (ownerUserId: string | null) => api.customers.update(customer.id, { ownerUserId }),
    onSuccess: () => {
      invalidate();
      setOwnerOpen(false);
    },
    onError: (err: any) => {
      const msg = apiMessage(err, 'Could not change the owner.');
      if (ownerOpen) setOwnerError(msg);
      else toast.error(msg);
    },
  });

  // GET /users is refused to sales; only an office reader ever asks.
  const usersQuery = useQuery({
    queryKey: ['users', 'crm-owner-options'],
    enabled: ownerOpen && canPickOwner,
    queryFn: () => api.users.list().then((r) => {
      const body = r.data;
      const rows = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return Array.isArray(rows) ? rows : [];
    }),
  });

  // CRM users are the people who can own an account. The CURRENT owner is
  // always an option even if their role has since changed — a select with no
  // option for its value shows the first option while state keeps the real one.
  const ownerOptions = useMemo(() => {
    const rows = (usersQuery.data ?? []) as any[];
    const opts = rows
      .filter((u) => (CRM_ROLES as readonly string[]).includes(u.role) || u.id === customer.ownerUserId)
      .map((u) => ({ id: u.id as string, name: (u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '—') as string }));
    if (customer.ownerUserId && !opts.some((o) => o.id === customer.ownerUserId)) {
      opts.push({ id: customer.ownerUserId, name: customer.ownerName || '—' });
    }
    return opts.sort((a, b) => a.name.localeCompare(b.name));
  }, [usersQuery.data, customer.ownerUserId, customer.ownerName]);

  const currentStatusLabel = isCustomerStatus(customer.status) ? CUSTOMER_STATUS_LABELS[customer.status] : '—';

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-xl border border-fieldiq-border bg-fieldiq-card p-4" data-testid="customer-account-strip">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Status</span>
        <CustomerStatusBadge status={customer.status} />
        {statusOptions.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setNextStatus(isCustomerStatus(customer.status) ? customer.status : '');
              setStatusError(null);
              statusMutation.reset();
              setStatusOpen(true);
            }}
            className="rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-1.5 text-xs font-semibold text-white hover:border-fieldiq-muted"
          >
            Change status
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">Owner</span>
        <span className="flex items-center gap-1 text-sm text-white">
          <UserRound size={14} className="text-fieldiq-muted" aria-hidden="true" />
          {customer.ownerName || '—'}
        </span>
        {canPickOwner && (
          <button
            type="button"
            onClick={() => {
              setNextOwner(customer.ownerUserId ?? '');
              setOwnerError(null);
              setOwnerOpen(true);
            }}
            className="rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-1.5 text-xs font-semibold text-white hover:border-fieldiq-muted"
          >
            Change owner
          </button>
        )}
        {canAssignSelf && (
          <button
            type="button"
            disabled={ownerMutation.isPending}
            onClick={() => ownerMutation.mutate(perms.userId)}
            className="flex items-center gap-1 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-1.5 text-xs font-semibold text-white hover:border-fieldiq-muted disabled:opacity-50"
          >
            <UserCheck size={14} aria-hidden="true" />
            Assign to me
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={statusOpen}
        title="Change status"
        confirmLabel="Change status"
        confirmVariant="primary"
        loading={statusMutation.isPending}
        onCancel={() => setStatusOpen(false)}
        onConfirm={() => {
          if (!nextStatus || nextStatus === customer.status) { setStatusOpen(false); return; }
          setStatusError(null);
          statusMutation.mutate(nextStatus);
        }}
        body={
          <div className="space-y-3">
            <p>
              {customer.name || 'This client'} is <strong>{currentStatusLabel}</strong>. The change is recorded with your
              name and the time.
            </p>
            <fieldset className="space-y-2">
              <legend className="sr-only">New status</legend>
              {statusOptions.map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-white">
                  <input
                    type="radio"
                    name="customer-status"
                    value={s}
                    checked={nextStatus === s}
                    onChange={() => setNextStatus(s)}
                  />
                  {CUSTOMER_STATUS_LABELS[s]}
                </label>
              ))}
            </fieldset>
            {statusError && (
              <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-sm text-fieldiq-text">
                {statusError}
              </p>
            )}
          </div>
        }
      />

      <ConfirmModal
        isOpen={ownerOpen}
        title="Change owner"
        confirmLabel="Save owner"
        confirmVariant="primary"
        loading={ownerMutation.isPending}
        onCancel={() => setOwnerOpen(false)}
        onConfirm={() => {
          const value = nextOwner || null;
          if (value === (customer.ownerUserId ?? null)) { setOwnerOpen(false); return; }
          setOwnerError(null);
          ownerMutation.mutate(value);
        }}
        body={
          <div className="space-y-3">
            <label htmlFor="customer-owner" className="block text-sm text-white">Owner</label>
            <select
              id="customer-owner"
              value={nextOwner}
              onChange={(e) => setNextOwner(e.target.value)}
              disabled={usersQuery.isLoading}
              className="w-full rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white"
            >
              <option value="">No owner</option>
              {ownerOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            {usersQuery.isError && (
              <p role="alert" className="text-sm text-fieldiq-text">Couldn't load the people list.</p>
            )}
            {ownerError && (
              <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-sm text-fieldiq-text">
                {ownerError}
              </p>
            )}
          </div>
        }
      />
    </div>
  );
}

export default CustomerAccountStrip;
